import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced auto-save for edit forms.
 *
 * Imperative + data-agnostic: call `schedule()` from your field-change handlers. After
 * `delay`ms of quiet it runs `saveFn()`, coalescing rapid edits into one call. `saveFn`
 * takes no arguments and should read the component's current state directly — the hook
 * always invokes the latest `saveFn` (kept in a ref), so there are no stale closures.
 *
 * Because saving is only triggered by `schedule()` (never by watching state), populating
 * the form from a freshly-loaded record does NOT trigger a save.
 *
 * On unmount (closing a modal / switching tabs) any pending edit is flushed.
 *
 * Use only for EDIT forms (existing records). Create/"New" forms keep an explicit button
 * and simply never call `schedule()`.
 *
 * @param {() => Promise<any>} saveFn  Persists current state. Should throw on failure.
 * @param {{delay?:number}} opts
 * @returns {{schedule:()=>void, flush:()=>Promise<void>, status:string}}
 *          status ∈ 'idle' | 'pending' | 'saving' | 'saved' | 'error'
 */
export default function useAutoSave(saveFn, { delay = 800 } = {}) {
  const [status, setStatus] = useState('idle');
  const timer = useRef(null);
  const dirty = useRef(false);
  const saveRef = useRef(saveFn);

  // Keep the latest saveFn so flush always saves current state.
  useEffect(() => { saveRef.current = saveFn; });

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!dirty.current) return;
    dirty.current = false;
    setStatus('saving');
    try {
      await saveRef.current();
      setStatus('saved');
    } catch (e) {
      dirty.current = true; // allow a later retry
      setStatus('error');
    }
  }, []);

  const schedule = useCallback(() => {
    dirty.current = true;
    setStatus('pending');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, delay);
  }, [flush, delay]);

  // Flush any pending edit on unmount (fire-and-forget).
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (dirty.current) { try { saveRef.current(); } catch (e) { /* ignore */ } }
  }, []);

  return { schedule, flush, status };
}

/** Maps an auto-save status to a short indicator label. */
export function autoSaveLabel(status) {
  switch (status) {
    case 'pending':
    case 'saving': return 'Saving…';
    case 'saved':  return 'All changes saved ✓';
    case 'error':  return 'Save failed — retrying on next edit';
    default:       return 'Auto-save on';
  }
}
